import { Component, inject, ViewChild } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { defer, forkJoin, iif, lastValueFrom, of } from "rxjs";
import { defaultIfEmpty, map } from "rxjs/operators";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import {
  SoapTemplate,
  SoapTemplateUpdate
} from "src/app/backend/models/soap-template";
import { ContainerLoadFn } from "src/app/components/ui/container/container-base/container-base.component";
import {
  ReportDesignerItemCustom,
  ReportDesignItem,
  ReportDesignItemConfig
} from "src/app/modules/bootstrap/modules/report/report-design-item";
import { sntTranslate } from "src/app/support/translate";
import { tryParseJson } from "src/app/support/json";
import { Configuration } from "src/app/backend/models/configuration";
import { ConfigurationBackend } from "src/app/backend/api/configuration.backend";
import { TemplateService } from "src/app/services/template.service";
import { ReportDesignerComponent } from "src/app/modules/bootstrap/modules/report/report-designer/report-designer.component";

import { getSoapTemplateSummaryHtml } from "src/app/support/health-record";
import { soapTemplateIsEmpty } from "src/app/support/soap-template";
import { isArray, isString, keys } from "lodash-es";
import {
  buildSoapDesignModel,
  SoapDesign,
  soapDesignKeyConfig,
  SoapDesignKeyConfigReturnType
} from "src/app/backend/models/soap/soap-design";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { TabService } from "src/app/services/tab.service";
import { routes } from "src/app/routing/routes";
import { sntConfirm } from "src/app/common/dialog";
import { injectId } from "src/app/support/inject";
import { MedicalServiceBackend } from "src/app/backend/api/medical-service/medical-service.backend";
import { MedicalService } from "src/app/backend/models/medical-service";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";
import {
  SoapSummaryConfigData,
  SoapTemplateSummaryComponent
} from "src/app/modules/bootstrap/pms-ui/modules/organization/soap-template/soap-template-editor/soap-template-summary/soap-template-summary.component";

type LoadData = { soapTemplate: SoapTemplate; configuration: Configuration };

@Component({
  standalone: false,
  selector: "pms-soap-template-editor",
  templateUrl: "soap-template-editor.component.html",
  styles: `
    :host {
      display: flex;
      flex: 1;
    }
  `
})
export class SoapTemplateEditorComponent {
  @ViewChild(ReportDesignerComponent)
  reportDesignerComponent!: ReportDesignerComponent;

  defaultItems!: ReportDesignItem[];

  loadFn: ContainerLoadFn<LoadData>;

  soapTemplate: SoapTemplate | undefined;

  headerHTML: string | undefined;
  footerHTML: string | undefined;

  private soapDesignLabelsConfig: SoapDesignKeyConfigReturnType =
    soapDesignKeyConfig();
  tablePropertyDataSource: Array<{ value: string; display: string }> = keys(
    buildSoapDesignModel(undefined, null, null)
  )
    .sort()
    .map((key: string) => ({
      value: key,
      display: this.soapDesignLabelsConfig[<keyof SoapDesign>key]
        ? this.soapDesignLabelsConfig[key].title
        : sntTranslate(key)
    }));

  private readonly id = injectId();
  private readonly configurationBackend = inject(ConfigurationBackend);
  private readonly memberBackend = inject(MemberBackend);
  private readonly medicalServiceBackend = inject(MedicalServiceBackend);
  private readonly templateService = inject(TemplateService);
  private readonly tabService = inject(TabService);
  private readonly patientBackend = inject(PetBackend);
  private readonly customerBackend = inject(CustomerBackend);
  private readonly physicianBackend = inject(PhysicianBackend);

  private lastTemplateConfig: ReportDesignItemConfig[] | undefined;

  constructor(private soapTemplateBackend: SoapTemplateBackend) {
    this.loadFn = () =>
      forkJoin({
        soapTemplate: this.soapTemplateBackend.load(this.id),
        configuration: this.configurationBackend.loadPms()
      });
  }

  onData(data: LoadData): void {
    this.soapTemplate = data.soapTemplate;
    this.headerHTML = data.configuration.header?.html;
    this.footerHTML = data.configuration.footer;

    this.defaultItems = this.getDefaultItems(this.soapTemplate);
  }

  onRawTemplateConfigChange(templateConfig: ReportDesignItemConfig[]): void {
    this.lastTemplateConfig = templateConfig;
  }

  async onTemplateConfigChange(
    templateConfig: ReportDesignItemConfig[]
  ): Promise<void> {
    const summaryData: SoapSummaryConfigData = <any>(
      templateConfig.find((x) => x.name === "soap-summary")!.config
    );

    const { staff, medicalService } = await lastValueFrom(
      forkJoin({
        medicalService: iif(
          () => !!summaryData.medicalServiceId,
          defer(() =>
            this.medicalServiceBackend
              .loadById(summaryData.medicalServiceId!)
              .pipe(
                map((medicalService: MedicalService) => ({
                  id: medicalService.id,
                  name: medicalService.name
                }))
              )
          ),
          of(undefined)
        ),
        staff: forkJoin(
          summaryData.staff.map((staffOrStaffId) =>
            this.memberBackend
              .load(
                isString(staffOrStaffId) ? staffOrStaffId : staffOrStaffId.id
              )
              .pipe(
                map((staff) => ({
                  id: staff.id,
                  number: staff.user?.number || "",
                  firstName: staff.firstName,
                  lastName: staff.lastName
                }))
              )
          )
        ).pipe(
          defaultIfEmpty<
            {
              id: string;
              number: string;
              firstName: string;
              lastName: string;
            }[],
            {
              id: string;
              number: string;
              firstName: string;
              lastName: string;
            }[]
          >([])
        )
      })
    );

    const subjectiveItems: ReportDesignItemConfig[] = [];
    const objectiveItems: ReportDesignItemConfig[] = [];
    const assessmentItems: ReportDesignItemConfig[] = [];
    const planItems: ReportDesignItemConfig[] = [];
    const signatureItems: ReportDesignItemConfig[] = [];

    templateConfig.forEach((item) => {
      switch (item.name) {
        case "soap-subjective": {
          subjectiveItems.push(item);
          break;
        }
        case "soap-objective": {
          objectiveItems.push(item);
          break;
        }
        case "soap-assessment": {
          assessmentItems.push(item);
          break;
        }
        case "soap-plan": {
          planItems.push(item);
          break;
        }
        case "soap-summary": {
          break;
        }
        default: {
          switch (item.type) {
            case "signature":
            case "customer-signature": {
              signatureItems.push(item);
              break;
            }
          }
          break;
        }
      }
    });

    const payload: SoapTemplateUpdate = {
      name: summaryData.title,
      enabled: this.soapTemplate!.enabled,
      note: summaryData.internalNote,
      staff: staff,
      tags: summaryData.tags || [],
      medicalService,
      detail: {
        subjective: JSON.stringify(subjectiveItems),
        assessment: JSON.stringify(assessmentItems),
        objective: JSON.stringify(objectiveItems),
        plan: JSON.stringify(planItems),
        signature: JSON.stringify(signatureItems)
      }
    };

    await lastValueFrom(
      this.soapTemplateBackend.update(this.soapTemplate!.id, payload)
    );
  }

  async onCancel(buttonClickAsyncEvent: ButtonClickAsyncEvent): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      const result = await sntConfirm(
        sntTranslate(marker("Modello soap")),
        sntTranslate(marker("Eliminare la bozza?"))
      );

      if (result) {
        await this.soapTemplateBackend.delete(this.id).toPromise();
      }

      await this.tabService.open(routes.organization.soap_templates.list());
      await this.tabService.close(
        routes.organization.soap_templates.id(this.soapTemplate!.id)
      );
    });
  }

  async onClose(buttonClickAsyncEvent: ButtonClickAsyncEvent): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      if (this.lastTemplateConfig) {
        await this.onTemplateConfigChange(this.lastTemplateConfig);
      }

      await this.tabService.open(routes.organization.soap_templates.list());
      await this.tabService.close(
        routes.organization.soap_templates.id(this.soapTemplate!.id)
      );
    });
  }

  async onPublish(buttonClickAsyncEvent: ButtonClickAsyncEvent): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      if (this.lastTemplateConfig) {
        await this.onTemplateConfigChange(this.lastTemplateConfig);
      }

      await this.soapTemplateBackend.publish(this.soapTemplate!.id).toPromise();

      await this.tabService.open(routes.organization.soap_templates.list());
      await this.tabService.close(
        routes.organization.soap_templates.id(this.soapTemplate!.id)
      );
    });
  }

  private getDefaultItems(data: SoapTemplate): ReportDesignItem[] {
    let subjectiveConfig = tryParseJson<any>(
      data.detail?.subjective,
      undefined
    );
    if (subjectiveConfig && isArray(subjectiveConfig)) {
      subjectiveConfig = subjectiveConfig[0];
    }

    let objectiveConfig = tryParseJson<any>(data.detail?.objective, [
      undefined
    ]);
    if (objectiveConfig && isArray(objectiveConfig)) {
      objectiveConfig = objectiveConfig[0];
    }

    let planConfig = tryParseJson<any>(data.detail?.plan, [undefined]);
    if (planConfig && isArray(planConfig)) {
      planConfig = planConfig[0];
    }

    let assessmentConfig = tryParseJson<any>(data.detail?.assessment, [
      undefined
    ]);
    if (assessmentConfig && isArray(assessmentConfig)) {
      assessmentConfig = assessmentConfig[0];
    }

    if (assessmentConfig) {
      if (assessmentConfig.config?.items) {
        assessmentConfig.config.items = assessmentConfig.config.items.filter(
          (x: any) => x.type !== "custom"
        );
      }

      if (assessmentConfig.baseConfig?.items) {
        assessmentConfig.baseConfig.items =
          assessmentConfig.baseConfig.items.filter(
            (x: { type: string }) => x.type !== "custom"
          );
      }
    }

    const signatureItems = tryParseJson<any>(data.detail?.signature, []);

    return [
      this.getSummaryReportDesignerItemCustom(data),
      {
        name: "soap-subjective",
        icon: "fa-light fa-s",
        title: sntTranslate(marker("Subjective")),
        type: "tab",
        extended: true,
        titleReadOnly: true,
        disableRemove: true,
        enableHideFeature: true,
        disableSortable: true,
        hideIconsSection: true,
        canAdd: (template: ReportDesignItemConfig[]) => {
          return !template.find((x) => x.name === "soap-subjective");
        },
        baseConfig: subjectiveConfig ? subjectiveConfig.config : undefined
      },
      {
        name: "soap-objective",
        icon: "fa-light fa-o",
        title: sntTranslate(marker("Objective")),
        type: "tab",
        extended: true,
        titleReadOnly: true,
        disableRemove: true,
        enableHideFeature: true,
        disableSortable: true,
        hideIconsSection: true,
        canAdd: (template: ReportDesignItemConfig[]) => {
          return !template.find((x) => x.name === "soap-objective");
        },
        baseConfig: objectiveConfig ? objectiveConfig.config : undefined
      },
      {
        name: "soap-assessment",
        icon: "fa-light fa-a",
        title: sntTranslate(marker("Assessment")),
        type: "tab",
        extended: true,
        titleReadOnly: true,
        disableRemove: true,
        enableHideFeature: true,
        disableSortable: true,
        hideIconsSection: true,
        canAdd: (template: ReportDesignItemConfig[]) => {
          return !template.find((x) => x.name === "soap-assessment");
        },
        baseConfig: assessmentConfig ? assessmentConfig.config : undefined
      },
      {
        name: "soap-plan",
        icon: "fa-light fa-p",
        title: sntTranslate(marker("Plan")),
        type: "tab",
        extended: true,
        titleReadOnly: true,
        disableRemove: true,
        enableHideFeature: true,
        disableSortable: true,
        hideIconsSection: true,
        canAdd: (template: ReportDesignItemConfig[]) => {
          return !template.find((x) => x.name === "soap-plan");
        },
        baseConfig: planConfig ? planConfig.config : undefined
      },
      ...signatureItems
    ];
  }

  private getSummaryReportDesignerItemCustom(
    data: SoapTemplate
  ): ReportDesignerItemCustom {
    return <ReportDesignerItemCustom>{
      name: "soap-summary",
      icon: "fa-light fa-c",
      title: sntTranslate(marker("Sommario")),
      type: "custom",
      extended: true,
      sticky: true,
      disableRemove: true,
      component: SoapTemplateSummaryComponent,
      hideIconsSection: true,
      showHeaderTitle: true,
      baseConfig: {
        preview: true,
        readOnly: this.soapTemplate!.published,
        templateEmpty: soapTemplateIsEmpty(data),
        title: data.name,
        internalNote: data.note,
        services: [],
        staff: data.staff?.map((x) => x.id),
        medicalService: data.medicalService,
        tags: data.tags || [],
        html: (item: ReportDesignerItemCustom) =>
          getSoapTemplateSummaryHtml(
            this.memberBackend,
            this.physicianBackend,
            this.templateService,
            item,
            this.patientBackend,
            this.customerBackend
          )
      }
    };
  }

  async onTogglePreviewSideSpace(): Promise<void> {
    await this.reportDesignerComponent.showPreview();
  }
}
